import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MotionCard from "../../shared/MotionCard";
import {
  getCalculationHistory,
  deleteCalculation,
} from "../../../services/enduser/calculatorService";

// ממשק נתוני חישוב בודד
interface Calculation {
  id: number;
  calculation_type: string;
  calculation_date: string;
  notes: string;
  input_data: any;
  result_data: any;
  display_info: {
    calculator_name: string;
    vehicle_price: number;
    currency: string;
    final_cost: number;
    total_taxes: number;
    calculation_date_formatted: string;
  };
}

// ממשק נתוני Pagination
interface PaginationInfo {
  offset: number;
  limit: number;
  total: number;
  returned: number;
  has_more: boolean;
}

const CalculationHistoryPage: React.FC = () => {
  const navigate = useNavigate();

  // State ראשי
  const [calculations, setCalculations] = useState<Calculation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationInfo>({
    offset: 0,
    limit: 50,
    total: 0,
    returned: 0,
    has_more: false,
  });

  // State לסינון
  const [selectedFilter, setSelectedFilter] = useState<string>("");

  // רשימת סוגי מחשבונים לסינון
  const calculatorTypes = [
    { value: "", label: "כל המחשבונים" },
    { value: "m1_vehicle", label: "M1 בנזין/דיזל - אישי" },
    { value: "M1_electric_private_import", label: "M1 חשמלי - אישי" },
    { value: "M1_vehicle_commercial", label: "M1 בנזין/דיזל - מסחרי" },
    { value: "M1_electric_commercial", label: "M1 חשמלי - מסחרי" },
    { value: "n1_tender", label: "N1 רגיל (טנדר) - אישי" },
    { value: "N1_electric_private_import", label: "N1 חשמלי - אישי" },
    { value: "N1N2_commercial", label: "N1 & N2 בנזין/דיזל - מסחרי" },
    { value: "N1_N2_electric_commercial", label: "N1 & N2 חשמלי - מסחרי" },
    { value: "trailer_vehicle", label: "נגרר" },
    { value: "taxi_vehicle", label: "מונית - אישי" },
    { value: "taxi_vehicle_commercial", label: "מונית - מסחרי" },
    { value: "motorcycle_vehicle", label: "אופנוע - אישי" },
    { value: "motorcycle_commercial", label: "אופנוע - מסחרי" },
    { value: "jet_ski_vehicle", label: "אופנוע ים - אישי" },
    { value: "jet_ski_commercial", label: "אופנוע ים - מסחרי" },
    { value: "collector_vehicle", label: "רכב אספנות" },
  ];

  // טעינה ראשונית
  useEffect(() => {
    loadHistory(0, selectedFilter);
  }, [selectedFilter]);

  /**
   * טעינת היסטוריה מהשרת
   */
  const loadHistory = async (offset: number, filter: string) => {
    try {
      if (offset === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError("");

      const response = await getCalculationHistory(
        offset,
        50,
        filter || undefined
      );

      if (response.res === "OK") {
        const newCalculations = response.data.calculations;
        const paginationInfo = response.data.pagination;

        if (offset === 0) {
          // טעינה ראשונית - החלף את הרשימה
          setCalculations(newCalculations);
        } else {
          // טעינה נוספת - הוסף לרשימה
          setCalculations((prev) => [...prev, ...newCalculations]);
        }

        setPagination(paginationInfo);
      } else {
        setError(response.msg || "שגיאה בטעינת ההיסטוריה");
      }
    } catch (err) {
      console.error("שגיאה בטעינת היסטוריה:", err);
      setError("שגיאה בטעינת ההיסטוריה");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  /**
   * טעינת עוד תוצאות
   */
  const handleLoadMore = () => {
    const nextOffset = pagination.offset + pagination.returned;
    loadHistory(nextOffset, selectedFilter);
  };

  /**
   * שינוי סינון
   */
  const handleFilter = (filterValue: string) => {
    setSelectedFilter(filterValue);
    // useEffect יטפל בטעינה מחדש
  };

  /**
   * מחיקת חישוב (מחיקה רכה)
   */
  const handleDelete = async (calculationId: number) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק חישוב זה?")) {
      return;
    }

    try {
      const response = await deleteCalculation(calculationId);

      if (response.res === "OK") {
        // הסרה מהרשימה המקומית
        setCalculations((prev) =>
          prev.filter((calc) => calc.id !== calculationId)
        );
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          returned: prev.returned - 1,
        }));
      } else {
        alert(response.msg || "שגיאה במחיקת החישוב");
      }
    } catch (err) {
      console.error("שגיאה במחיקת חישוב:", err);
      alert("שגיאה במחיקת החישוב");
    }
  };

  /**
   * צפייה בחישוב מלא
   */
  const handleViewCalculation = (calculation: Calculation) => {
    // שמירה ב-localStorage למעבר לדף התוצאות
    localStorage.setItem("viewed_calculation", JSON.stringify(calculation));

    // ניווט לדף תוצאות
    navigate(`/enduser/calculation-result/${calculation.id}`);
  };

  /**
   * פורמט תאריך לתצוגה ידידותית
   */
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  /**
   * פורמט מספר עם פסיקים
   */
  const formatNumber = (num: number): string => {
    return num.toLocaleString("he-IL");
  };
  // המשך של הקומפוננטה - החלק של ה-JSX
  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            📜 היסטוריית חישובים
          </h1>
          <p className="text-gray-600">כל החישובים שביצעת במערכת</p>
        </div>

        {/* סינון */}
        <MotionCard className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label
                htmlFor="calculator-filter"
                className="text-gray-700 font-medium"
              >
                🔍 סינון:
              </label>
              <select
                id="calculator-filter"
                value={selectedFilter}
                onChange={(e) => handleFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="בחירת סוג מחשבון לסינון"
              >
                {calculatorTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600">
              סה"כ:{" "}
              <span className="font-bold text-blue-600">
                {pagination.total}
              </span>{" "}
              חישובים
            </div>
          </div>
        </MotionCard>

        {/* שגיאה */}
        {error && (
          <MotionCard className="mb-6 bg-red-50 border-red-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <p className="text-red-700">{error}</p>
            </div>
          </MotionCard>
        )}

        {/* Loading ראשוני */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">טוען היסטוריה...</p>
          </div>
        )}

        {/* רשימה ריקה */}
        {!isLoading && calculations.length === 0 && (
          <MotionCard className="text-center py-12">
            <span className="text-6xl mb-4 block">📭</span>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              אין חישובים שמורים
            </h2>
            <p className="text-gray-600 mb-6">
              בצע חישוב ראשון במערכת כדי לראות אותו כאן
            </p>
            <button
              onClick={() => navigate("/enduser/dashboard")}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              חזור לדשבורד
            </button>
          </MotionCard>
        )}

        {/* רשימת חישובים */}
        {!isLoading && calculations.length > 0 && (
          <div className="space-y-4">
            {calculations.map((calc) => (
              <MotionCard
                key={calc.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewCalculation(calc)}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* פרטי החישוב */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900 mb-1">
                          {calc.display_info.calculator_name}
                        </h2>
                        <p className="text-sm text-gray-600">
                          🗓️ {formatDate(calc.calculation_date)}
                        </p>
                      </div>
                    </div>

                    {/* מידע כלכלי */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">ערך רכב</p>
                        <p className="text-lg font-bold text-blue-700">
                          {formatNumber(calc.display_info.vehicle_price)}{" "}
                          {calc.display_info.currency}
                        </p>
                      </div>

                      <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">סך מיסים</p>
                        <p className="text-lg font-bold text-red-700">
                          ₪{formatNumber(calc.display_info.total_taxes)}
                        </p>
                      </div>

                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">עלות כוללת</p>
                        <p className="text-lg font-bold text-green-700">
                          ₪{formatNumber(calc.display_info.final_cost)}
                        </p>
                      </div>
                    </div>

                    {/* הערות */}
                    {calc.notes && (
                      <div className="bg-yellow-50 border-r-4 border-yellow-400 p-3 rounded">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">💭 הערות:</span>{" "}
                          {calc.notes}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* כפתורי פעולה */}
                  <div className="flex lg:flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCalculation(calc);
                      }}
                      className="flex-1 lg:flex-none bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      👁️ צפייה
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(calc.id);
                      }}
                      className="flex-1 lg:flex-none bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      🗑️ מחיקה
                    </button>
                  </div>
                </div>
              </MotionCard>
            ))}
          </div>
        )}

        {/* כפתור "הצג עוד" */}
        {!isLoading && pagination.has_more && (
          <div className="mt-8 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoadingMore ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  טוען...
                </>
              ) : (
                <>
                  📥 הצג עוד (
                  {pagination.total - (pagination.offset + pagination.returned)}{" "}
                  נותרו)
                </>
              )}
            </button>
          </div>
        )}

        {/* כפתור חזרה */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/enduser/dashboard")}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← חזור לדשבורד
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalculationHistoryPage;
